"use client";

import { Link } from "@frontend/lib/link";
import { usePathname } from "@frontend/lib/navigation";
import { cx } from "@frontend/components/ui";

type NavItem = {
  href: string;
  label: string;
  /** Shorter label on narrow screens. */
  shortLabel?: string;
  /** When true, pathname must equal href exactly — not merely start with it. */
  exact?: boolean;
  mobile?: boolean;
};

const ITEMS: NavItem[] = [
  { href: "/overview", label: "Overview", shortLabel: "Home", exact: true, mobile: true },
  { href: "/collabs", label: "Collabs", mobile: true },
  { href: "/work-history", label: "History", mobile: true },
  { href: "/settings/team", label: "Access", exact: true, mobile: false },
];

function isActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.href;
  if (item.href === "/collabs") {
    return pathname === "/collabs" || /^\/collabs\/[0-9a-f-]{36}$/i.test(pathname);
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

function NavLinks({
  items,
  pathname,
  compact,
  actionCount,
}: {
  items: NavItem[];
  pathname: string;
  compact?: boolean;
  actionCount: number;
}) {
  return (
    <>
      {items.map((item) => {
        const active = isActive(pathname, item);
        const label = compact && item.shortLabel ? item.shortLabel : item.label;
        const showBadge = item.href === "/collabs" && actionCount > 0;

        return (
          <Link
            key={item.href}
            href={item.href}
            aria-current={active ? "page" : undefined}
            aria-label={
              showBadge
                ? `${label}, ${actionCount} need${actionCount === 1 ? "s" : ""} your action`
                : undefined
            }
            className={cx(
              "inline-flex items-center gap-1.5 rounded-lg font-medium transition",
              compact ? "px-2.5 py-2 text-sm" : "px-3 py-2 text-sm",
              active ? "bg-canvas text-ink" : "text-ink-muted hover:bg-canvas/70 hover:text-ink",
            )}
          >
            {label}
            {showBadge ? (
              <span className="tabular min-w-[1.25rem] rounded-full bg-brand-600 px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none text-canvas">
                {actionCount > 99 ? "99+" : actionCount}
              </span>
            ) : null}
          </Link>
        );
      })}
    </>
  );
}

export function WorkspaceNav({
  showAccess,
  actionCount = 0,
}: {
  showAccess: boolean;
  /** Collabs waiting on the signed-in side — shown as a badge on Collabs. */
  actionCount?: number;
}) {
  const pathname = usePathname();
  const items = showAccess ? ITEMS : ITEMS.filter((item) => item.href !== "/settings/team");

  return (
    <>
      <nav className="hidden items-center gap-0.5 sm:flex">
        <NavLinks items={items} pathname={pathname} actionCount={actionCount} />
      </nav>
      <nav className="flex items-center gap-0.5 sm:hidden">
        <NavLinks
          items={items.filter((item) => item.mobile)}
          pathname={pathname}
          compact
          actionCount={actionCount}
        />
      </nav>
    </>
  );
}
